import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  FAIR_PAGE_LOADER_MAX_MS,
  FAIR_PAGE_LOADER_MIN_MS,
} from '@/ui/tokens/fair-page-loader.data'

/**
 * Global branded page transition loader.
 * Shown for routes with `meta.pageLoader`; dismissed via `markReady()`
 * (when products/data loaded) or max timeout / navigation error.
 * Visual: monogram line-draw (stroke-dashoffset) — see FairPageLoaderMark.
 */
export const useFairPageLoaderStore = defineStore('fairPageLoader', () => {
  const visible = ref(false)
  const sessionId = ref(0)
  const startedAt = ref(0)
  const waitingForReady = ref(false)

  let hideTimer: ReturnType<typeof setTimeout> | null = null
  let maxTimer: ReturnType<typeof setTimeout> | null = null

  const isActive = computed(() => waitingForReady.value)

  function clearTimers() {
    if (hideTimer != null) {
      clearTimeout(hideTimer)
      hideTimer = null
    }
    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = null
    }
  }

  function hide() {
    clearTimers()
    waitingForReady.value = false
    visible.value = false
  }

  function start() {
    clearTimers()
    sessionId.value += 1
    startedAt.value = Date.now()
    waitingForReady.value = true
    visible.value = true

    const session = sessionId.value
    maxTimer = setTimeout(() => {
      if (sessionId.value === session && waitingForReady.value) {
        hide()
      }
    }, FAIR_PAGE_LOADER_MAX_MS)
  }

  function markReady() {
    if (!waitingForReady.value) return

    waitingForReady.value = false
    const session = sessionId.value
    const elapsed = Date.now() - startedAt.value
    const remaining = Math.max(0, FAIR_PAGE_LOADER_MIN_MS - elapsed)

    if (maxTimer != null) {
      clearTimeout(maxTimer)
      maxTimer = null
    }

    hideTimer = setTimeout(() => {
      if (sessionId.value === session) {
        visible.value = false
        hideTimer = null
      }
    }, remaining)
  }

  function fail() {
    hide()
  }

  return {
    visible,
    sessionId,
    isActive,
    start,
    markReady,
    fail,
    hide,
  }
})

export const useFairPageLoader = useFairPageLoaderStore
